import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList, Legend } from 'recharts';
import { formatLargeNumber } from '../../utils/helpers';

// Professional Grouped Bar Chart showing emissions across all lifecycle stages
const EmissionsStageBarChart = ({ emissions }) => {
  const safe = (v) => (typeof v === 'number' && isFinite(v) ? v : 0);

  const a1 = safe(emissions.total_a1);
  const a2 = safe(emissions.total_a2);
  const a3 = safe(emissions.total_a3);
  const a4a5 = safe(emissions.total_a4_a5 || 0);
  const b2b5 = safe(emissions.total_b2_b5 || 0);
  const c1c4 = safe(emissions.total_c1_c4 || 0);

  const data = [
    { 
      name: 'A1\nRaw Materials', 
      value: a1,
      color: '#3B82F6'
    },
    { 
      name: 'A2\nTransport', 
      value: a2,
      color: '#60A5FA'
    },
    { 
      name: 'A3\nManufacturing', 
      value: a3,
      color: '#93C5FD'
    },
    { 
      name: 'A4-A5\nConstruction', 
      value: a4a5,
      color: '#8B5CF6'
    },
    { 
      name: 'B2-B5\nUse Phase', 
      value: b2b5,
      color: '#10B981'
    },
    { 
      name: 'C1-C4\nEnd of Life', 
      value: c1c4,
      color: '#F59E0B'
    }
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{d.name.replace('\n', ' ')}</p>
          <p className="tooltip-value">{formatLargeNumber(d.value, { unit: 'kg CO₂e', decimals: 2 })}</p>
        </div>
      );
    }
    return null;
  };

  const CustomLabel = (props) => {
    const { x, y, width, height, value } = props;
    
    if (!value || value === 0) return null;
    
    return (
      <text 
        x={x + width / 2} 
        y={y - 8} 
        fill="#475569" 
        textAnchor="middle" 
        fontSize="11"
        fontWeight="600"
      >
        {formatLargeNumber(value, { decimals: 1 })}
      </text>
    );
  };

  return (
    <div className="chart-container">
      {/* <h3>Lifecycle Stage Emissions Comparison</h3> */}
      <ResponsiveContainer width="100%" height={320}>
        <BarChart
          data={data}
          margin={{ top: 24, right: 24, left: 8, bottom: 48 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
          <XAxis 
            dataKey="name" 
            tick={{ fontSize: 11, fill: '#475569', textAnchor: 'middle' }}
            interval={0}
            angle={0}
          />
          <YAxis 
            tick={{ fontSize: 11, fill: '#475569' }}
            tickFormatter={(value) => formatLargeNumber(value, { decimals: 1 })}
            label={{ value: 'kg CO₂e', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#475569' } }}
          />
          <Tooltip content={<CustomTooltip />} />
          
          <Bar 
            dataKey="value" 
            radius={[6, 6, 0, 0]}
            maxBarSize={80}
          >
            <LabelList content={<CustomLabel />} />
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default EmissionsStageBarChart;
